import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Download, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { slides, companyInfo } from '../../data/mockSlides';
import SlideContainer from './SlideContainer';
import TitleSlide from './slides/TitleSlide';
import ProblemSlide from './slides/ProblemSlide';
import SolutionSlide from './slides/SolutionSlide';
import ProductSlide from './slides/ProductSlide';
import TechnologySlide from './slides/TechnologySlide';
import RnDSlide from './slides/RnDSlide';
import MarketSlide from './slides/MarketSlide';
import TractionSlide from './slides/TractionSlide';
import TeamSlide from './slides/TeamSlide';
import AWSSlide from './slides/AWSSlide';
import RoadmapSlide from './slides/RoadmapSlide';
import AskSlide from './slides/AskSlide';

const slideComponents = {
  title: TitleSlide,
  problem: ProblemSlide,
  solution: SolutionSlide,
  product: ProductSlide,
  technology: TechnologySlide,
  rnd: RnDSlide,
  market: MarketSlide,
  traction: TractionSlide,
  team: TeamSlide,
  aws: AWSSlide,
  roadmap: RoadmapSlide,
  ask: AskSlide,
};

const PresentationViewer = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const totalSlides = slides.length;
  const progress = ((currentSlide + 1) / totalSlides) * 100;

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < totalSlides) {
      setDirection(index > currentSlide ? 1 : -1);
      setCurrentSlide(index);
    }
  }, [currentSlide, totalSlides]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1);
    }
  }, [currentSlide, totalSlides, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  }, [currentSlide, goToSlide]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Home') {
        goToSlide(0);
      } else if (e.key === 'End') {
        goToSlide(totalSlides - 1);
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, goToSlide, totalSlides]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const currentSlideData = slides[currentSlide];
  const SlideComponent = slideComponents[currentSlideData.type];

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">V</span>
            </div>
            <span className="text-white font-semibold hidden sm:block">{companyInfo.name}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">
              {currentSlide + 1} / {totalSlides}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => goToSlide(0)}
              className="text-slate-400 hover:text-white"
            >
              <Home className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-slate-400 hover:text-white"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-1 rounded-none bg-slate-800" />
      </header>

      {/* Main Content */}
      <main className="flex-1 relative pt-16 pb-20 overflow-hidden">
        <SlideContainer slideId={currentSlide} direction={direction}>
          {SlideComponent && <SlideComponent data={currentSlideData} />}
        </SlideContainer>
      </main>

      {/* Navigation Controls */}
      <footer className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-t border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Slide Thumbnails */}
          <div className="hidden md:flex items-center gap-1 overflow-x-auto max-w-2xl">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goToSlide(index)}
                className={`w-12 h-8 rounded text-xs font-medium transition-all ${
                  currentSlide === index
                    ? 'bg-cyan-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {/* Nav Buttons */}
          <div className="flex items-center gap-3 ml-auto">
            <Button
              variant="outline"
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              onClick={nextSlide}
              disabled={currentSlide === totalSlides - 1}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:opacity-90 disabled:opacity-50"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PresentationViewer;
